import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { v4 as uuidv4 } from "uuid";
import { uploadFile } from "@s/api/file";
import "./GridQuizEditor.css";

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

var LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

function makeEmptyGrid(size) {
  var grid = [];
  for (var i = 0; i < size; i++) {
    var row = [];
    for (var j = 0; j < size; j++) {
      row.push("");
    }
    grid.push(row);
  }
  return grid;
}

function getWordCells(answer, row0, col0, dir) {
  var cells = [];
  for (var i = 0; i < answer.length; i++) {
    if (dir === "h") cells.push([row0, col0 + i]);
    else cells.push([row0 + i, col0]);
  }
  return cells;
}

function isOutOfBounds(answer, row0, col0, dir, size) {
  if (dir === "h") return col0 + answer.length > size;
  return row0 + answer.length > size;
}

function hasConflict(grid, answer, row0, col0, dir) {
  for (var i = 0; i < answer.length; i++) {
    var r = dir === "h" ? row0 : row0 + i;
    var c = dir === "h" ? col0 + i : col0;
    var existing = grid[r][c];
    if (existing && existing !== answer[i]) return true;
  }
  return false;
}

function applyWord(grid, answer, row0, col0, dir) {
  var next = grid.map(function(row) { return row.slice(); });
  for (var i = 0; i < answer.length; i++) {
    var r = dir === "h" ? row0 : row0 + i;
    var c = dir === "h" ? col0 + i : col0;
    next[r][c] = answer[i];
  }
  return next;
}

function wouldFormAnswer(grid, r, c, letter, answerList) {
  var size = grid.length;
  var temp = grid.map(function(row) { return row.slice(); });
  temp[r][c] = letter;

  var rowStr = temp[r].join("");
  for (var i = 0; i < answerList.length; i++) {
    if (rowStr.indexOf(answerList[i]) !== -1) return true;
  }
  var colStr = temp.map(function(row) { return row[c]; }).join("");
  for (var j = 0; j < answerList.length; j++) {
    if (colStr.indexOf(answerList[j]) !== -1) return true;
  }
  return false;
}

function fillRandomLetters(grid, words) {
  var answerList = words.map(function(w) { return w.answer.toUpperCase(); });
  var size = grid.length;
  var next = grid.map(function(row) { return row.slice(); });

  for (var r = 0; r < size; r++) {
    for (var c = 0; c < size; c++) {
      if (next[r][c]) continue;
      var letter;
      var attempts = 0;
      do {
        letter = LETTERS[Math.floor(Math.random() * LETTERS.length)];
        attempts++;
      } while (attempts < 20 && wouldFormAnswer(next, r, c, letter, answerList));
      next[r][c] = letter;
    }
  }
  return next;
}

// ─────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────

function ClueMediaField(props) {
  var label = props.label;
  var accept = props.accept;
  var value = props.value;
  var onChange = props.onChange;
  var onClear = props.onClear;
  var supportingText = props.supportingText;
  var onSupportingTextChange = props.onSupportingTextChange;

  return (
    <div className="clue-row">
      <span className="clue-label">{label}</span>
      {value ? (
        <div className="clue-media-preview">
          {accept === "image/*" ? (
            <img src={value} alt="clue" className="clue-image-thumb" />
          ) : (
            <audio controls src={value} className="clue-audio-player" />
          )}
          <button className="clue-remove-media" onClick={onClear}>Remove</button>
        </div>
      ) : (
        <label className="clue-upload-label">
          + Upload {label}
          <input type="file" accept={accept} className="clue-file-input" onChange={onChange} />
        </label>
      )}
      {value && (
        <input
          className="clue-supporting-text"
          type="text"
          placeholder={"Supporting text for " + label.toLowerCase() + " (optional)"}
          value={supportingText || ""}
          onChange={onSupportingTextChange}
        />
      )}
    </div>
  );
}

function WordCard(props) {
  var word = props.word;
  var index = props.index;
  var isPlacing = props.isPlacing;
  var isExpanded = props.isExpanded;
  var onStartPlace = props.onStartPlace;
  var onUpdate = props.onUpdate;
  var onRemove = props.onRemove;
  var onFileUpload = props.onFileUpload;
  var onToggleExpand = props.onToggleExpand;

  var isPlaced = word.row !== null && word.col !== null;

  var cardClass = "word-entry-card";
  if (isPlacing) cardClass += " word-entry-active";
  else if (isPlaced) cardClass += " word-entry-placed";
  if (!isExpanded) cardClass += " word-entry-collapsed";

  var placeBtnClass = "place-btn";
  if (isPlacing) placeBtnClass += " placing";
  else if (isPlaced) placeBtnClass += " placed";

  var placeBtnLabel = "Place in Grid";
  if (isPlacing) placeBtnLabel = "Click grid cell...";
  else if (isPlaced) placeBtnLabel = "Re-place";

  return (
    <div className={cardClass}>
      <button
        className="word-remove-btn"
        onClick={function() { onRemove(index); }}
        title="Remove word"
      >
        x
      </button>

      <div className="word-entry-top">
        <span className="word-index-badge" onClick={onToggleExpand} style={{ cursor: "pointer" }}>
          {index + 1}
        </span>
        <input
          className="word-answer-input"
          type="text"
          maxLength={15}
          placeholder="ANSWER"
          value={word.answer}
          onChange={function(e) {
            onUpdate(index, "answer", e.target.value.toUpperCase().replace(/[^A-Z]/g, ""));
          }}
        />
        <button
          className={placeBtnClass}
          onClick={function() { onStartPlace(index); }}
          disabled={!word.answer}
          title={word.answer ? "Click a cell in the grid to place this word" : "Enter answer first"}
        >
          {placeBtnLabel}
        </button>
        <button
          className="word-expand-btn"
          onClick={onToggleExpand}
          title={isExpanded ? "Collapse details" : "Expand details"}
        >
          {isExpanded ? "▲" : "▼"}
        </button>
      </div>

      {isExpanded && isPlaced && (
        <div className="word-position-info">
          Position: Row {word.row}, Col {word.col} — {word.dir === "h" ? "Horizontal" : "Vertical"}
        </div>
      )}

      {isExpanded && (
        <div className="clue-section">
          <div className="clue-row">
            <span className="clue-label">Clue Text</span>
            <textarea
              className="clue-text-input"
              placeholder="Enter text clue (optional)"
              value={word.clue_text}
              onChange={function(e) { onUpdate(index, "clue_text", e.target.value); }}
            />
          </div>

          <ClueMediaField
            label="Clue Image"
            accept="image/*"
            value={word.clue_image}
            onChange={function(e) { onFileUpload(index, "clue_image", e); }}
            onClear={function() { onUpdate(index, "clue_image", null); }}
            supportingText={word.clue_image_text}
            onSupportingTextChange={function(e) { onUpdate(index, "clue_image_text", e.target.value); }}
          />

          <ClueMediaField
            label="Clue Audio"
            accept="audio/*"
            value={word.clue_audio}
            onChange={function(e) { onFileUpload(index, "clue_audio", e); }}
            onClear={function() { onUpdate(index, "clue_audio", null); }}
            supportingText={word.clue_audio_text}
            onSupportingTextChange={function(e) { onUpdate(index, "clue_audio_text", e.target.value); }}
          />
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// Main Editor
// ─────────────────────────────────────────────

var QuizType5McqEditor = forwardRef(function(props, ref) {
  var existingData = props.existingData;
  var quizType = props.quizType;
  var isCrossword = quizType === 5;

  var _gridSize = useState(10);
  var gridSize = _gridSize[0];
  var setGridSize = _gridSize[1];

  var _baseGrid = useState(function() { return makeEmptyGrid(10); });
  var baseGrid = _baseGrid[0];
  var setBaseGrid = _baseGrid[1];

  var _displayGrid = useState(function() { return makeEmptyGrid(10); });
  var displayGrid = _displayGrid[0];
  var setDisplayGrid = _displayGrid[1];

  var _words = useState([]);
  var words = _words[0];
  var setWords = _words[1];

  var _placingIndex = useState(null);
  var placingIndex = _placingIndex[0];
  var setPlacingIndex = _placingIndex[1];

  var _placingDir = useState("h");
  var placingDir = _placingDir[0];
  var setPlacingDir = _placingDir[1];

  var _previewCells = useState([]);
  var previewCells = _previewCells[0];
  var setPreviewCells = _previewCells[1];

  var _conflictCells = useState([]);
  var conflictCells = _conflictCells[0];
  var setConflictCells = _conflictCells[1];

  var _error = useState("");
  var error = _error[0];
  var setError = _error[1];

  var _fillerGenerated = useState(false);
  var fillerGenerated = _fillerGenerated[0];
  var setFillerGenerated = _fillerGenerated[1];

  var _expandedIndex = useState(0);
  var expandedIndex = _expandedIndex[0];
  var setExpandedIndex = _expandedIndex[1];

  var hasLoadedRef = useRef(false);

  // ── Populate from existing data ──
  useEffect(function() {
    if (!existingData || hasLoadedRef.current) return;
    hasLoadedRef.current = true;

    var size = existingData.size || existingData.length || 10;
    setGridSize(size);
    var wordList = (existingData.words || []).map(function(w) {
      return Object.assign({}, w, {
        id: w.id || uuidv4(),
        clue_text: w.clue_text || "",
        clue_image: w.clue_image || null,
        clue_image_text: w.clue_image_text || "",
        clue_audio: w.clue_audio || null,
        clue_audio_text: w.clue_audio_text || "",
      });
    });
    setWords(wordList);

    var grid = makeEmptyGrid(size);
    wordList.forEach(function(w) {
      if (w.row !== null && w.col !== null && w.dir) {
        var r0 = w.row - 1;
        var c0 = w.col - 1;
        if (!isOutOfBounds(w.answer, r0, c0, w.dir, size)) {
          grid = applyWord(grid, w.answer, r0, c0, w.dir);
        }
      }
    });
    setBaseGrid(grid);

    if (existingData.grid) {
      var restoredGrid = existingData.grid.map(function(row) { return row.slice(); });
      setDisplayGrid(restoredGrid);
      setFillerGenerated(!isCrossword);
    } else {
      setDisplayGrid(grid.map(function(r) { return r.slice(); }));
    }
  }, [existingData, isCrossword]);

  // ── Rebuild base grid ──
  var rebuildBaseGrid = useCallback(function(wordList, size) {
    var grid = makeEmptyGrid(size);
    wordList.forEach(function(w) {
      if (w.row !== null && w.col !== null && w.dir) {
        var r0 = w.row - 1;
        var c0 = w.col - 1;
        if (!isOutOfBounds(w.answer, r0, c0, w.dir, size)) {
          grid = applyWord(grid, w.answer, r0, c0, w.dir);
        }
      }
    });
    setBaseGrid(grid);
    setDisplayGrid(grid.map(function(r) { return r.slice(); }));
    setFillerGenerated(false);
  }, []);

  // ── Grid size change ──
  var handleGridSizeChange = function(e) {
    var valStr = e.target.value;
    if (valStr === "") {
      setGridSize("");
      return;
    }
    var val = parseInt(valStr, 10);
    if (isNaN(val)) return;

    setGridSize(val);

    if (val >= 3 && val <= 15) {
      var empty = makeEmptyGrid(val);
      setBaseGrid(empty);
      setDisplayGrid(empty);
      setFillerGenerated(false);
      setWords(function(prev) {
        return prev.map(function(w) {
          return Object.assign({}, w, { row: null, col: null, dir: "h" });
        });
      });
      setPlacingIndex(null);
      setPreviewCells([]);
    }
  };

  var handleGridSizeBlur = function() {
    var val = parseInt(gridSize, 10);
    if (isNaN(val) || val < 3) val = 3;
    if (val > 15) val = 15;

    if (val !== gridSize) {
      setGridSize(val);
      var empty = makeEmptyGrid(val);
      setBaseGrid(empty);
      setDisplayGrid(empty);
      setFillerGenerated(false);
      setWords(function(prev) {
        return prev.map(function(w) {
          return Object.assign({}, w, { row: null, col: null, dir: "h" });
        });
      });
      setPlacingIndex(null);
      setPreviewCells([]);
    }
  };

  // ── Add word ──
  var addWord = function() {
    setWords(function(prev) {
      var nextList = prev.concat([{
        id: uuidv4(),
        answer: "",
        row: null,
        col: null,
        dir: "h",
        clue_text: "",
        clue_image: null,
        clue_image_text: "",
        clue_audio: null,
        clue_audio_text: "",
      }]);
      setExpandedIndex(nextList.length - 1);
      return nextList;
    });
  };

  // ── Remove word ──
  var removeWord = function(idx) {
    setWords(function(prev) {
      var next = prev.filter(function(_, i) { return i !== idx; });
      rebuildBaseGrid(next, baseGrid.length);
      if (expandedIndex >= next.length) {
        setExpandedIndex(Math.max(0, next.length - 1));
      }
      return next;
    });
    if (placingIndex === idx) setPlacingIndex(null);
  };

  // ── Toggle word expansion ──
  var toggleExpand = function(idx) {
    if (expandedIndex === idx) {
      setExpandedIndex(-1); // collapse all
    } else {
      setExpandedIndex(idx);
    }
  };

  // ── Update word field ──
  var updateWord = function(idx, field, value) {
    setWords(function(prev) {
      var next = prev.map(function(w, i) {
        if (i !== idx) return w;
        return Object.assign({}, w, { [field]: value });
      });
      if (field === "answer") {
        var updated = next.map(function(w, i) {
          if (i !== idx) return w;
          return Object.assign({}, w, { row: null, col: null });
        });
        rebuildBaseGrid(updated, baseGrid.length);
        return updated;
      }
      return next;
    });
  };

  // ── File upload ──
  var handleFileUpload = function(idx, field, e) {
    var file = e.target.files[0];
    if (!file) return;
    var reader = new FileReader();
    reader.onloadend = function() { updateWord(idx, field, reader.result); };
    reader.readAsDataURL(file);
    uploadFile(file).then(function(res) { updateWord(idx, field, res.file); });
  };

  // ── Start placing ──
  var startPlacing = function(idx) {
    if (!words[idx] || !words[idx].answer) return;
    setPlacingIndex(idx);
    setPlacingDir("h");
    setPreviewCells([]);
    setConflictCells([]);
    setError("");
  };

  // ── Cell hover ──
  var handleCellHover = useCallback(function(r, c) {
    if (placingIndex === null) return;
    var word = words[placingIndex];
    if (!word || !word.answer) return;
    var answer = word.answer;

    if (isOutOfBounds(answer, r, c, placingDir, baseGrid.length)) {
      setPreviewCells([]);
      setConflictCells([]);
      return;
    }

    var cells = getWordCells(answer, r, c, placingDir);
    var conflict = hasConflict(baseGrid, answer, r, c, placingDir);
    if (conflict) {
      setConflictCells(cells.map(function(pair) { return { r: pair[0], c: pair[1] }; }));
      setPreviewCells([]);
    } else {
      setPreviewCells(cells.map(function(pair) { return { r: pair[0], c: pair[1] }; }));
      setConflictCells([]);
    }
  }, [placingIndex, words, placingDir, baseGrid]);

  // ── Cell click ──
  var handleCellClick = useCallback(function(r, c) {
    if (placingIndex === null) return;
    var word = words[placingIndex];
    if (!word || !word.answer) return;
    var answer = word.answer;

    if (isOutOfBounds(answer, r, c, placingDir, baseGrid.length)) {
      setError("Word \"" + answer + "\" would go out of bounds from that cell.");
      return;
    }
    if (hasConflict(baseGrid, answer, r, c, placingDir)) {
      setError("Letter conflict — another word occupies that position with a different letter.");
      return;
    }

    setWords(function(prev) {
      var next = prev.map(function(w, i) {
        if (i !== placingIndex) return w;
        return Object.assign({}, w, { row: r + 1, col: c + 1, dir: placingDir });
      });
      rebuildBaseGrid(next, baseGrid.length);
      return next;
    });
    setPlacingIndex(null);
    setPreviewCells([]);
    setConflictCells([]);
    setError("");
  }, [placingIndex, words, placingDir, baseGrid, rebuildBaseGrid]);

  // ── Generate fillers (Crossword only) ──
  var generateFillers = function() {
    var filled = fillRandomLetters(baseGrid, words);
    setDisplayGrid(filled);
    setFillerGenerated(true);
  };

  // ── Cell classification map ──
  var cellInfo = useMemo(function() {
    var info = {};
    var setInfo = function(r, c, cls) {
      var key = r + "-" + c;
      info[key] = Object.assign({}, info[key] || {}, cls);
    };
    words.forEach(function(w, wi) {
      if (w.row === null || w.col === null) return;
      var cells = getWordCells(w.answer, w.row - 1, w.col - 1, w.dir);
      cells.forEach(function(pair) { setInfo(pair[0], pair[1], { isAnswer: true, wordIdx: wi }); });
    });
    previewCells.forEach(function(cell) { setInfo(cell.r, cell.c, { isPreview: true }); });
    conflictCells.forEach(function(cell) { setInfo(cell.r, cell.c, { isConflict: true }); });
    return info;
  }, [words, previewCells, conflictCells]);

  // ── Ref API ──
  useImperativeHandle(ref, function() {
    return {
      getFormattedData: function() {
        var grid = isCrossword ? baseGrid : displayGrid;
        return {
          size: baseGrid.length,
          grid: grid.map(function(row) { return row.slice(); }),
          words: words.map(function(w) {
            return {
              answer: w.answer,
              row: w.row,
              col: w.col,
              dir: w.dir,
              clue_text: w.clue_text,
              clue_image: w.clue_image || null,
              clue_image_text: w.clue_image_text || null,
              clue_audio: w.clue_audio || null,
              clue_audio_text: w.clue_audio_text || null,
            };
          }),
        };
      },

      validateData: function() {
        if (gridSize < 3 || gridSize > 15) {
          return { valid: false, message: "Grid size must be between 3 and 15." };
        }
        if (words.length === 0) {
          return { valid: false, message: "At least one word is required." };
        }
        for (var i = 0; i < words.length; i++) {
          var w = words[i];
          if (!w.answer.trim()) {
            return { valid: false, message: "Word " + (i + 1) + ": answer is empty." };
          }
          if (w.row === null || w.col === null) {
            return { valid: false, message: "Word " + (i + 1) + " (\"" + w.answer + "\") has not been placed in the grid." };
          }
          if (!w.clue_text.trim() && !w.clue_image && !w.clue_audio) {
            return { valid: false, message: "Word " + (i + 1) + " (\"" + w.answer + "\") needs at least one clue (text, image, or audio)." };
          }
        }
        if (!isCrossword && !fillerGenerated) {
          return { valid: false, message: "Please generate filler letters before saving (Word Finder requires a complete grid)." };
        }
        return { valid: true };
      },
    };
  });

  // ── Render grid ──
  var renderGrid = function() {
    var cells = [];
    var actualSize = baseGrid.length;
    for (var r = 0; r < actualSize; r++) {
      for (var c = 0; c < actualSize; c++) {
        var key = r + "-" + c;
        var info = cellInfo[key] || {};
        var letter = (displayGrid[r] && displayGrid[r][c]) || "";

        var cls = "grid-cell";
        if (info.isConflict) cls += " cell-conflict";
        else if (info.isPreview) cls += " cell-preview";
        else if (info.isAnswer) cls += " cell-answer cell-placed";
        else if (letter) cls += " cell-filler";

        if (placingIndex !== null) cls += " cell-placing-target";

        // Capture r,c for event handlers (closure-safe)
        (function(rowIdx, colIdx, cellKey, cellCls, cellLetter) {
          cells.push(
            <div
              key={cellKey}
              className={cellCls}
              onClick={function() { handleCellClick(rowIdx, colIdx); }}
              onMouseEnter={function() { handleCellHover(rowIdx, colIdx); }}
              onMouseLeave={function() { setPreviewCells([]); setConflictCells([]); }}
            >
              {cellLetter}
            </div>
          );
        }(r, c, key, cls, letter));
      }
    }
    return cells;
  };

  var placingWord = placingIndex !== null ? words[placingIndex] : null;
  var allWordsPlaced = words.length > 0 && words.every(function(w) { return w.row !== null; });

  return (
    <div className="grid-quiz-editor">
      {/* Top controls */}
      <div className="grid-quiz-controls-bar">
        <label className="grid-quiz-size-label">Grid Size (N x N):</label>
        <input
          className="grid-size-input"
          type="number"
          min={3}
          max={15}
          value={gridSize}
          onChange={handleGridSizeChange}
          onBlur={handleGridSizeBlur}
        />
        {!isCrossword && (
          <button
            className="grid-fill-btn"
            onClick={generateFillers}
            disabled={!allWordsPlaced}
            title="Auto-fill remaining cells with random letters"
          >
            {fillerGenerated ? "Re-generate Fillers" : "Generate Filler Letters"}
          </button>
        )}
        {!isCrossword && fillerGenerated && (
          <span className="grid-ready-label">
            Grid ready — {baseGrid.length}x{baseGrid.length}
          </span>
        )}
        {isCrossword && (
          <span className="grid-wf-label">
            Crossword — student types answers into empty cells
          </span>
        )}
      </div>

      {error && <div className="grid-quiz-error">{error}</div>}

      {/* Placement banner */}
      {placingIndex !== null && (
        <div className="placement-banner">
          <span>
            Placing: <strong>{placingWord && placingWord.answer}</strong> — click a grid cell
          </span>
          <button
            className={"placement-dir-btn" + (placingDir === "h" ? " active" : "")}
            onClick={function() { setPlacingDir("h"); }}
          >
            H (Right)
          </button>
          <button
            className={"placement-dir-btn" + (placingDir === "v" ? " active" : "")}
            onClick={function() { setPlacingDir("v"); }}
          >
            V (Down)
          </button>
          <button
            className="placement-cancel-btn"
            onClick={function() { setPlacingIndex(null); setPreviewCells([]); }}
          >
            Cancel
          </button>
        </div>
      )}

      <div className="grid-quiz-body">
        {/* Top: Grid */}
        <div className="grid-quiz-left-panel">
          <div
            className="grid-canvas"
            style={{ gridTemplateColumns: "repeat(" + baseGrid.length + ", 34px)" }}
          >
            {renderGrid()}
          </div>
        </div>

        {/* Bottom: Words */}
        <div className="grid-quiz-right-panel">
          <div className="word-list-header">
            <span className="word-list-title">
              Words ({words.length}) — {isCrossword ? "Crossword" : "Word Finder"}
            </span>
            <button className="add-word-btn" onClick={addWord}>
              + Add Word
            </button>
          </div>

          {words.length === 0 && (
            <div className="word-list-empty">
              No words yet. Click &quot;Add Word&quot; to start.
            </div>
          )}

          {words.map(function(word, idx) {
            return (
              <WordCard
                key={word.id}
                word={word}
                index={idx}
                isPlacing={placingIndex === idx}
                isExpanded={expandedIndex === idx}
                onStartPlace={startPlacing}
                onUpdate={updateWord}
                onRemove={removeWord}
                onFileUpload={handleFileUpload}
                onToggleExpand={function() { toggleExpand(idx); }}
                quizType={quizType}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
});

QuizType5McqEditor.displayName = "QuizType5McqEditor";

export default QuizType5McqEditor;
